import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../lib/api.js'
import { getApiErrorMessage } from '../lib/apiErrors.js'
import { useToast } from '../components/Toast/ToastProvider.jsx'

/** Ceiling for polling backoff — a failing query never waits longer than this. */
export const POLL_MAX_INTERVAL = 10 * 60 * 1000

/**
 * Validates that an API method exists in the api object
 * @param {string} method - The API method name
 * @throws {Error} If method is invalid or not found
 */
function validateApiMethod(method) {
  if (typeof method !== 'string') {
    throw new Error(
      `Invalid API method key: expected string, got ${typeof method}`,
    )
  }
  if (!api[method]) {
    const availableMethods = Object.keys(api).join(', ')
    throw new Error(
      `API method "${method}" not found. Available methods: ${availableMethods}`,
    )
  }
  if (typeof api[method] !== 'function') {
    throw new Error(
      `API method "${method}" is not a function. Check api object definition.`,
    )
  }
}

/** Accepts `poll: 60000` or `poll: { interval, maxInterval, enabled }`. */
function normalizePoll(poll) {
  if (!poll) return null
  const cfg = typeof poll === 'number' ? { interval: poll } : poll
  if (!cfg.interval) return null
  return {
    interval: cfg.interval,
    maxInterval: cfg.maxInterval ?? POLL_MAX_INTERVAL,
    enabled: cfg.enabled !== false,
  }
}

/** +/-20% so many open tabs don't retry in lockstep and stampede on recovery. */
const rollJitter = () => 0.8 + Math.random() * 0.4

/**
 * Custom hook for React Query GET operations
 *
 * Wraps @tanstack/react-query's useQuery with:
 * - API method validation
 * - Automatic error toasts
 * - Success callbacks
 * - Consistent configuration defaults
 * - Polling with backoff (`poll`)
 *
 * @param {Object} config - Query configuration
 * @param {Array} config.key - Query key [method, params] (e.g., ['getLanes', { search: 'test' }])
 * @param {*} config.payload - Data to pass to API method
 * @param {boolean} [config.enabled=true] - Whether query should run
 * @param {number|Object} [config.poll] - Poll this query. A number is the
 *   interval in ms; the object form takes `{ interval, maxInterval, enabled }`.
 *   Owns the whole polling policy so call sites don't hand-roll it:
 *   - Never polls a hidden tab (`refetchIntervalInBackground: false`), and
 *     refetches once when the tab becomes visible again so a returning user
 *     isn't left waiting out the (possibly backed-off) interval. Note React
 *     Query keys both off document VISIBILITY, not window focus — a visible
 *     but unfocused window keeps polling and does not trigger a refetch.
 *   - Backs off exponentially while the query keeps failing
 *     (interval x 2^failures, capped at `maxInterval`), with +/-20% jitter so
 *     that many open tabs don't retry in lockstep and stampede a backend that
 *     has just recovered. One success returns it to `interval`.
 *   - Only the FIRST failure of a consecutive run raises an error toast. A
 *     backend that is down for an hour is worth saying once, not sixty times.
 *     `refetch()` always toasts, so a manual retry is never silent.
 *   Set `enabled: false` to pause — required for any query whose data feeds
 *   `form.reset()`, otherwise a tick discards whatever the user has typed.
 * @param {boolean} [config.keepPreviousData=false] - Keep showing the previous
 *   key's data while the new key fetches (placeholderData). isLoading stays
 *   true during that fetch so consumers can overlay a loader on stale data.
 * @param {Function} [config.onError] - Error callback. Always fires, even when
 *   the toast is suppressed.
 * @param {Function} [config.onSuccess] - Success callback
 * @param {boolean} [config.showToast=true] - Whether to show error toast
 * @param {string} [config.toastType='error'] - Toast type for errors
 * @param {...Object} config.queryOptions - Additional React Query options
 *
 * @returns {Object} React Query result object, with `refetch` wrapped so manual
 *   retries always surface their error. Use React Query's own `isLoadingError`
 *   (failed with nothing cached) and `isRefetchError` (failed over cached data)
 *   to tell a blocking failure from a stale one.
 *
 * @example
 * const { data: lanes, isLoading } = useApiQuery({
 *   key: ['getLanes', { search }],
 *   payload: { search },
 *   enabled: true,
 * })
 *
 * @see REACT_QUERY_IMPLEMENTATION.md for detailed usage patterns
 */
export function useApiQuery(config) {
  const {
    key,
    payload,
    enabled = true,
    poll,
    keepPreviousData = false,
    onError,
    onSuccess,
    showToast = true,
    toastType = 'error',
    ...queryOptions
  } = config

  const addToast = useToast()
  const queryClient = useQueryClient()

  // Validate query key format
  if (!Array.isArray(key)) {
    throw new Error('Query key must be an array')
  }
  if (key.length === 0) {
    throw new Error('Query key cannot be empty')
  }

  // Validate API method exists
  const [method] = key
  validateApiMethod(method)

  const pollConfig = normalizePoll(poll)
  const isPolled = !!pollConfig
  const pollInterval = pollConfig?.interval
  const pollMaxInterval = pollConfig?.maxInterval
  const pollEnabled = pollConfig?.enabled ?? false

  // Consecutive-failure count for the backoff. Deliberately state, not a ref:
  // React Query only recomputes refetchInterval when the component re-renders
  // (setOptions) or the query dispatches. A ref written from an effect updates
  // *after* that recompute, so the first backed-off interval would be skipped
  // and the poll would run one more time at full speed. Setting state forces
  // the extra render that makes the new interval take effect immediately.
  const [pollFailures, setPollFailures] = useState(0)
  const baselineRef = useRef(0)
  const jitterRef = useRef(1)

  // React Query restarts the interval timer whenever this returns a different
  // value (queryObserver #updateTimers runs on every state dispatch), so the
  // jitter factor is frozen per failure level rather than rolled on each read
  // — a fresh random number each time would reset the timer forever and the
  // poll would never fire.
  const refetchInterval = useCallback(() => {
    // No `!pollInterval` guard: this is only wired into useQuery when isPolled
    // is true, which already requires a truthy interval. A second check there
    // would be unreachable by construction.
    if (!pollEnabled) return false
    if (pollFailures === 0) return pollInterval
    const backedOff = Math.min(
      pollInterval * 2 ** pollFailures,
      pollMaxInterval,
    )
    return Math.round(backedOff * jitterRef.current)
  }, [pollInterval, pollMaxInterval, pollEnabled, pollFailures])

  // Execute query
  const result = useQuery({
    queryKey: key,
    queryFn: () => api[method](payload),
    enabled,
    staleTime: 1000 * 60, // 1 minute default
    refetchOnWindowFocus: false,
    retry: 1,
    ...(keepPreviousData && { placeholderData: (prev) => prev }),
    ...queryOptions,
    ...(isPolled && {
      refetchInterval,
      refetchIntervalInBackground: false,
      // Returning to the tab is the strongest signal that someone wants fresh
      // data, and without it the backoff can leave them staring at stale rows
      // for up to maxInterval. Cheap: React Query gates this on the tab
      // actually becoming visible AND the data being older than staleTime.
      // Tied to pollEnabled so pausing for a dirty form pauses this too — a
      // focus refetch would clobber edits exactly like a poll tick.
      refetchOnWindowFocus: pollEnabled,
    }),
  })

  const { error, isError, isSuccess, data, errorUpdateCount, dataUpdatedAt } =
    result

  // Refs to prevent duplicate callbacks/toasts
  const prevErrorRef = useRef(null)
  const prevDataRef = useRef(null)
  // Armed by the wrapped refetch so a user-initiated retry is never silent,
  // however deep into a failure run it happens.
  const manualRefetchRef = useRef(false)

  // --- poll state upkeep. Declaration order matters: these must settle before
  // the toast effect reads the failure count. ---

  // errorUpdateCount is monotonic for the query's lifetime, so consecutive
  // failures are its distance from the last success. (fetchFailureCount cannot
  // be used here — React Query resets it at the start of every fetch, so it
  // counts retries within one fetch, not failures across fetches.)
  const consecutiveFailures = Math.max(
    0,
    errorUpdateCount - baselineRef.current,
  )

  // A new query key starts a new failure run.
  const keyHash = JSON.stringify(key)
  useEffect(() => {
    baselineRef.current = 0
    jitterRef.current = 1
    setPollFailures(0)
  }, [keyHash])

  // Any successful fetch ends the run. Rebaselining (rather than decrementing
  // a counter) keeps this idempotent under StrictMode's double invocation.
  useEffect(() => {
    if (dataUpdatedAt > 0) {
      baselineRef.current = errorUpdateCount
      jitterRef.current = 1
      manualRefetchRef.current = false
      setPollFailures(0)
    }
    // Keyed on dataUpdatedAt ALONE. errorUpdateCount is read, not tracked: it
    // only changes on failures, and re-running this then would re-baseline the
    // very count the backoff is measuring, flattening it back to zero.
  }, [dataUpdatedAt])

  // Publish the count so refetchInterval recomputes with the new backoff.
  useEffect(() => {
    if (consecutiveFailures > 0 && consecutiveFailures !== pollFailures) {
      jitterRef.current = rollJitter()
      setPollFailures(consecutiveFailures)
    }
  }, [consecutiveFailures, pollFailures])

  // Error handling effect (React Query v5 compliant)
  useEffect(() => {
    if (isError && error && error !== prevErrorRef.current) {
      prevErrorRef.current = error

      const wasManual = manualRefetchRef.current
      manualRefetchRef.current = false
      // A polled query that keeps failing says so once. Everything else, and
      // every manual retry, behaves exactly as before. Derived from
      // errorUpdateCount rather than the published state, which still holds
      // the previous render's value at this point.
      const firstOfRun = consecutiveFailures <= 1
      const announce = wasManual || !isPolled || firstOfRun

      if (showToast && announce) {
        const errorMessage =
          getApiErrorMessage(error) || error.message || 'An error occurred'
        addToast(errorMessage, toastType)
      }
      onError?.(error)
    }

    if (!isError) {
      prevErrorRef.current = null
    }
  }, [
    isError,
    error,
    showToast,
    toastType,
    addToast,
    onError,
    isPolled,
    consecutiveFailures,
  ])

  // Success handling effect
  useEffect(() => {
    if (isSuccess && data && data !== prevDataRef.current) {
      prevDataRef.current = data
      onSuccess?.(data)
    }
  }, [isSuccess, data, onSuccess])

  const baseRefetch = result.refetch
  const refetch = useCallback(
    (...args) => {
      manualRefetchRef.current = true
      return baseRefetch(...args)
    },
    [baseRefetch],
  )

  const queryState = queryClient.getQueryState(key)
  const isLoading =
    result.isLoading ||
    (!!queryState?.isInvalidated && result.isFetching) ||
    // Key changed while showing previous data (keepPreviousData) — still loading.
    // isPlaceholderData guard keeps polling refetches from flipping this on.
    (result.isPlaceholderData && result.isFetching)

  return { ...result, isLoading, refetch }
}
